/**
 * The one password rule (AUTH-15): at least 8 characters, with a letter and a number. The API
 * checks it on every new password and publishes it (GET /api/v1/auth/password-policy); forms show
 * the published rules through usePasswordRules. PASSWORD_RULES is the same rule, used until the
 * published one has loaded, or when it can't be.
 */

export interface PasswordRule {
  key: string;
  label: string;
  test: (password: string) => boolean;
}

export const PASSWORD_RULES: PasswordRule[] = [
  // Characters, not UTF-16 units: an emoji counts once, as it does on the server.
  { key: "length", label: "At least 8 characters", test: (p) => Array.from(p).length >= 8 },
  { key: "letter", label: "At least one letter", test: (p) => /[A-Za-z]/.test(p) },
  { key: "number", label: "At least one number", test: (p) => /[0-9]/.test(p) },
];

export const passesAll = (rules: PasswordRule[], password: string): boolean =>
  rules.every((r) => r.test(password));

/** The published policy's rules, or null when the answer can't be used (keep PASSWORD_RULES). */
export function rulesFromPolicy(policy: unknown): PasswordRule[] | null {
  const rules = (policy as { rules?: unknown } | null | undefined)?.rules;
  if (!Array.isArray(rules) || rules.length === 0) return null;
  try {
    return rules.map((r) => {
      if (typeof r?.key !== "string" || typeof r?.label !== "string" || typeof r?.pattern !== "string") {
        throw new Error("unusable rule");
      }
      const pattern = new RegExp(r.pattern, "u");
      return { key: r.key, label: r.label, test: (p: string) => pattern.test(p) };
    });
  } catch {
    return null;
  }
}
